// MenuContext.tsx
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { CartItem } from '../components/menu/types';
import { useTenant } from './TenantContext';

export interface OrderLineItem {
    productId: string;
    baseProductId: string;
    variantId?: string;
    name: string;
    variantName?: string;
    price: number;
    quantity: number;
    comment?: string;
}

export interface ActiveOrderSnapshot {
    orderCode: string;
    tableNumber: string;
    status?: string;
    isPaid?: boolean;
    totalAmount?: number;
    customerName?: string;
    orderItems?: OrderLineItem[];
    lastUpdatedAt?: string;
}

interface MenuContextType {
    cart: CartItem[];
    addToCart: (item: CartItem) => void;
    incrementItem: (productId: string) => void;
    decrementItem: (productId: string) => void;
    removeFromCart: (productId: string) => void;
    clearCart: () => void;
    setItemComment: (productId: string, comment: string) => void;
    activeOrder: ActiveOrderSnapshot | null;
    setActiveOrder: (order: ActiveOrderSnapshot | null) => void;
}

const MenuContext = createContext<MenuContextType | undefined>(undefined);

export const useMenu = () => useContext(MenuContext);

export const MenuProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const tenantCtx = useTenant();
    const tenantId = tenantCtx?.tenantId || null;
    const storageKey = tenantId ? `tenant-order-${tenantId}` : null;

    const [cart, setCart] = useState<CartItem[]>([]);
    const [activeOrder, setActiveOrderState] = useState<ActiveOrderSnapshot | null>(null);
    const [hydrated, setHydrated] = useState(false);

    useEffect(() => {
        if (!storageKey) {
            setCart([]);
            setActiveOrderState(null);
            setHydrated(true);
            return;
        }
        try {
            const raw = localStorage.getItem(storageKey);
            if (raw) {
                const parsed = JSON.parse(raw);
                const normalizedCart = Array.isArray(parsed?.cart)
                    ? (parsed.cart as any[]).map((item) => {
                        if (!item || typeof item !== 'object') return null;
                        const productId = typeof item.productId === 'string' ? item.productId : String(item.productId);
                        const baseProductId = item.baseProductId
                            || (productId.includes('__v_') ? productId.split('__v_')[0] : productId);
                        const variantId = item.variantId
                            ?? (productId.includes('__v_') ? productId.split('__v_')[1] : undefined);
                        return {
                            ...item,
                            productId,
                            baseProductId,
                            variantId,
                        } as CartItem;
                    }).filter(Boolean) as CartItem[]
                    : [];
                setCart(normalizedCart);
                if (parsed?.activeOrder && typeof parsed.activeOrder === 'object') {
                    setActiveOrderState(parsed.activeOrder as ActiveOrderSnapshot);
                } else {
                    setActiveOrderState(null);
                }
            } else {
                setCart([]);
                setActiveOrderState(null);
            }
        } catch (error) {
            console.warn('Impossibile leggere il carrello locale:', error);
            setCart([]);
            setActiveOrderState(null);
        } finally {
            setHydrated(true);
        }
    }, [storageKey]);

    useEffect(() => {
        if (!hydrated || !storageKey) return;
        try {
            const payload = JSON.stringify({ cart, activeOrder });
            localStorage.setItem(storageKey, payload);
        } catch (error) {
            console.warn('Impossibile salvare il carrello locale:', error);
        }
    }, [hydrated, storageKey, cart, activeOrder]);

    const addToCart = useCallback((item: CartItem) => {
        setCart(current => {
            const idx = current.findIndex(i => i.productId === item.productId);
            if (idx > -1) {
                const updated = [...current];
                updated[idx] = {
                    ...updated[idx],
                    quantity: updated[idx].quantity + item.quantity,
                    comment: item.comment ?? updated[idx].comment,
                    price: item.price,
                    variantName: item.variantName ?? updated[idx].variantName
                };
                return updated;
            }
            return [...current, { ...item }];
        });
    }, []);

    const incrementItem = useCallback((productId: string) => {
        setCart(current => {
            const idx = current.findIndex(i => i.productId === productId);
            if (idx === -1) return current;
            const updated = [...current];
            updated[idx] = {
                ...updated[idx],
                quantity: updated[idx].quantity + 1
            };
            return updated;
        });
    }, []);

    const decrementItem = useCallback((productId: string) => {
        setCart(current => {
            const idx = current.findIndex(i => i.productId === productId);
            if (idx === -1) return current;
            const target = current[idx];
            if (target.quantity <= 1) {
                return current.filter(item => item.productId !== productId);
            }
            const updated = [...current];
            updated[idx] = { ...target, quantity: target.quantity - 1 };
            return updated;
        });
    }, []);

    const removeFromCart = useCallback((productId: string) => {
        setCart(current => current.filter(item => item.productId !== productId));
    }, []);

    const clearCart = useCallback(() => {
        setCart([]);
    }, []);

    const setItemComment = useCallback((productId: string, comment: string) => {
        setCart(current => {
            const idx = current.findIndex(i => i.productId === productId);
            if (idx === -1) return current;
            const updated = [...current];
            updated[idx] = { ...updated[idx], comment };
            return updated;
        });
    }, []);

    const setActiveOrder = useCallback((order: ActiveOrderSnapshot | null) => {
        setActiveOrderState(order);
    }, []);

    const contextValue = useMemo<MenuContextType>(() => ({
        cart,
        addToCart,
        incrementItem,
        decrementItem,
        removeFromCart,
        clearCart,
        setItemComment,
        activeOrder,
        setActiveOrder
    }), [cart, addToCart, incrementItem, decrementItem, removeFromCart, clearCart, setItemComment, activeOrder, setActiveOrder]);

    return (
        <MenuContext.Provider value={contextValue}>
            {children}
        </MenuContext.Provider>
    );
};
